import { useCallback, useMemo, useState } from "react";

const FilteredButton = ({ positionInGroup, ...rest }: any) => {
  const { color, className, children, ...buttonProps } = rest;

  return (
    <button
      {...buttonProps}
      className={`min-h-[38px] rounded-md border px-3 py-1.5 transition-colors ${
        className ?? ""
      }`}
    >
      {children}
    </button>
  );
};

interface Props {
  previousLabel?: React.ReactNode;
  nextLabel?: React.ReactNode;
  pageCount: number;
  onPageChange: (...args: any) => void;
  pageRangeDisplayed?: number;
  breakLabel?: string;
  pageLinkClassName?: string;
  previousClassName?: string;
  nextClassName?: string;
  disabledClassName?: string;
  breakClassName?: string;
  containerClassName?: string;
  activeClassName?: string;
  gap?: Record<string, string>;
  forcePage?: number;
}

const CustomPagination = ({
  previousLabel = "Prev",
  nextLabel = "Next",
  pageCount,
  onPageChange,
  pageRangeDisplayed = 2,
  breakLabel = "...",
  pageLinkClassName,
  previousClassName,
  nextClassName,
  disabledClassName,
  breakClassName,
  containerClassName,
  activeClassName,
  gap,
  forcePage,
}: Props) => {
  const [currentPage, setCurrentPage] = useState(forcePage || 0);

  const handlePageClick = useCallback(
    (page: number) => {
      onPageChange({ selected: page });
      setCurrentPage(page);
    },
    [onPageChange]
  );

  const pages = useMemo(() => {
    const pages = [];
    const leftSide = pageRangeDisplayed / 2;
    const rightSide = pageRangeDisplayed - leftSide;

    for (let i = 0; i < pageCount; i++) {
      const label = i + 1;
      let className = pageLinkClassName;
      if (i === currentPage) className += ` ${activeClassName}`;

      if (
        i === 0 ||
        i === pageCount - 1 ||
        (i >= currentPage - leftSide && i <= currentPage + rightSide)
      ) {
        pages.push(
          <FilteredButton
            color={"light"}
            key={i}
            className={`md:inline-flex hidden ${
              i === currentPage
                ? "bg-blue-50 border border-blue-500 text-blue-600"
                : "bg-white border border-gray-300 text-gray-500"
            } flex-col justify-center items-center text-center text-sm font-medium ${className}`}
            onClick={() => handlePageClick(i)}
            style={gap}
          >
            {label}
          </FilteredButton>
        );
      } else if (
        (i < currentPage - leftSide && i === 1) ||
        (i > currentPage + rightSide && i === pageCount - 2)
      ) {
        pages.push(
          <FilteredButton
            color={"light"}
            key={i}
            className={
              breakClassName +
              " flex-col justify-center items-center md:inline-flex hidden text-center text-sm font-medium font-figtree leading-tight"
            }
            style={gap}
          >
            {breakLabel}
          </FilteredButton>
        );
      }
    }

    return pages;
  }, [
    pageRangeDisplayed,
    pageCount,
    pageLinkClassName,
    currentPage,
    activeClassName,
    gap,
    handlePageClick,
    breakClassName,
    breakLabel,
  ]);

  return (
    <div className={containerClassName}>
      <div className="flex flex-row">
        <FilteredButton
          color={"light"}
          onClick={() => handlePageClick(currentPage - 1)}
          className="flex flex-col items-center justify-center"
          disabled={currentPage === 0}
          style={gap}
        >
          {previousLabel}
        </FilteredButton>

        {pages}

        <FilteredButton
          color={"light"}
          disabled={currentPage === pageCount - 1}
          onClick={() => handlePageClick(currentPage + 1)}
          className="flex flex-col items-center justify-center"
        >
          {nextLabel}
        </FilteredButton>
      </div>
    </div>
  );
};

export default CustomPagination;
